<template>
  <div class="flex mb-12 lpath-form">
    <div class="w-3/5 content-left-panel">
      <div class="flex flex-wrap justify-end name-field">
        <vs-input class="w-full" label="Learning Path name" v-model="localLPath.name" />
      </div>
      <div class="flex flex-wrap justify-between mt-10">
        <div class="w-full">
          <label class="vs-input--label">Slug</label>
          <vx-input-group class="mb-base" prependClasses="border border-solid border-grey border-r-0">
            <template slot="prepend">
              <div class="prepend-text">
                <vs-switch v-model="autoAlias" :checked="autoAlias" class="auto-alias-checkbox" />
              </div>
            </template>

            <vs-input
              class="auto-alias-field"
              v-model="localLPath.slug"
              :disabled="autoAlias"
            />
          </vx-input-group>
        </div>
      </div>
      <div class="w-full flex">
        <div class="flex w-1/2 px-10 mt-10">
          <vs-switch v-model="localLPath.is_featured" :checked="localLPath.is_featured" class="mr-5 is-featured-field" />
          <label>Is featured</label>
        </div>
      </div>
      <div class="flex flex-wrap md:mt-10 mt-6">
        <Skills :content-type="CONTENT_TYPES.LEARNING_PATH" :skills-list="localLPath.taxonomy_skills" :editMode="false" />
      </div>
      <div class="w-full mt-10">
        <vs-textarea
          class="mb-0 md:mt-10 mt-6 bg-white"
          rows="3"
          label="Description"
          v-model="localLPath.description"
        />
      </div>
      <div id="lpath-async-api-data-2" class="flex mt-10 flex-wrap vs-con-loading__container">
        <div class="w-full pb-5">
          <label class="vs-input--label">Category</label>
          <v-select
            multiple
            v-model="localLPath.categories"
            placeholder="Select a category"
            :reduce="category => category.value"
            :options="getCategories"
          />
        </div>
        <div class="w-1/2 pb-5 pr-3">
          <label class="vs-input--label">Difficulty</label>
          <v-select
            v-model="localLPath.difficulty"
            placeholder="Select a level"
            :reduce="difficulty => difficulty.value"
            :options="getLPathDifficulties"
          />
        </div>
        <div class="w-1/2 pb-5 pl-3">
          <label class="vs-input--label">Learning Area</label>
          <v-select
            multiple
            v-model="localLPath.learning_areas"
            placeholder="Select an area"
            :options="getLAreas"
            :reduce="la => la.value"
          />
        </div>
        <div class="w-full pb-5">
          <label class="vs-input--label">Tags</label>
          <v-select
            v-model="localLPath.tags"
            multiple
            taggable
            push-tags
            placeholder="Select one or more tags"
            :options="tagsOptions"
            :filterable="false"
            @search="onSearchTags"
          >
            <template slot="no-options">type to search or add a tag</template>
          </v-select>
        </div>
        <div class="w-full pb-5">
          <label class="vs-input--label">Next Learning Path</label>
          <v-select
            v-model="localLPath.next_learning_path"
            placeholder="Select next Learning Path"
            :options="lpathsOptions"
            :filterable="false"
            :reduce="lp => lp.value"
            @search="onSearchLPaths"
          >
            <template slot="no-options">type to search</template>
          </v-select>
        </div>
        <div class="flex w-full flex-wrap pb-5">
          <label class="vs-input--label">Related Collection</label>
          <v-select
            class="w-full"
            v-model="relatedCollectionValue"
            placeholder="Select collections related"
            :options="fullCollectionsOptions"
            :filterable="false"
            @search="onSearchLightCollections"
            :reduce="(collection) => collection.value"
            :clearSearchOnBlur="clearSearch"
          >
            <template slot="no-options">type to search a collection</template>
          </v-select>
        </div>
        <div class="w-1/2 pb-5 pr-3">
          <label class="vs-input--label">Closing JWPlayer Id</label>
          <vs-input class="w-full" v-model="localLPath.closing_video_jwplayer_id" description-text="JWPlayer media id, Example: 'mcAbYS1s'"></vs-input>
        </div>
        <div class="w-1/2 pb-5 pl-3">
          <label class="vs-input--label">Closing video Remarks</label>
          <vs-input class="w-full" v-model="localLPath.closing_video_remarks"></vs-input>
        </div>
        <div class="w-full pb-5 pr-3" v-if="localLPath.vendors && localLPath.vendors.length">
          <label class="vs-input--label">Vendors</label>
          <div class="flex">
            <div class="mr-5" v-for="vendor in localLPath.vendors" :key="vendor">
              <vs-chip transparent>
                {{getVendorsEntries[vendor].name}}
              </vs-chip >
            </div>
          </div>
        </div>
        <div class="w-full pb-5 pr-3" v-if="localLPath.instructors && localLPath.instructors.length">
          <label class="vs-input--label">Instructors</label>
          <div class="flex flex-wrap">
            <div class="mr-5" v-for="instructor in localLPath.instructors" :key="instructor">
              <vx-tooltip delay="1s" v-if="getInstructorsEntries[instructor]" :text="getInstructorsEntries[instructor].email || getInstructorsEntries[instructor].name">
                <vs-chip transparent>
                  <vs-avatar :src="getInstructorsEntries[instructor].profile_img_url"/>
                  {{getInstructorsEntries[instructor].name}}
                </vs-chip >
              </vx-tooltip>
            </div>
          </div>
        </div>
      </div>
      <vs-alert color="danger" icon-pack="feather" icon="icon-info" class="mb-5" v-if="localLPath.content && !localLPath.content.length && rememberToSave">
        Please save the Learning Path before upload another CSV
      </vs-alert>
      <div class="flex flex-wrap mt-10 structure-import" v-if="canUploadCSV">
        <label class="w-full vs-input--label">Learning Path structure</label>
        <upload-file
          v-if="!rememberToSave && false"
          automatic
          v-model="localLPath.structureFiles"
          class="w-full pl-0"
          text="Upload the lpath structure"
          @on-success="successUpload"
          @on-error="errorUpload"
          :uploadMethod="uploadLPathCSVWrapper"
        />
        <div class="flex cursor-pointer w-1/4 mb-5 add-grouper mt-5" @click.stop="() => {addGroup({parent: null, index: 0, type: 'group'})}">
          <feather-icon
            icon="PlusCircleIcon"
            svgClasses='h-5 w-5 color-current text-grey'
            class='flex items-center ml-1 mr-2'
          />
          Add a Group
        </div>
      </div>
      <div class="mt-5 tree-structure" v-if="localLPath.content && localLPath.content.length">
        <div class="my-10" v-if="importCsvErrors && importCsvErrors.length">
          <ul class="import-csv-errors pl-10">
            <li v-for="([error, video], index) in importCsvErrors" :key="index">
              <span class="text-danger">{{error}}</span>
              in {{video}}
            </li>
          </ul>
        </div>
        <vs-alert color="warning" icon-pack="feather" icon="icon-info" class="mb-5" v-if="rememberToSave">
          Please remember to save the lpath after edit the structure.
        </vs-alert>
        <l-path-structure
          :content="localLPath.content"
          :namespace="'lpaths'"
          :duration="localLPath.duration_in_seconds"
          :videos="getVideoEntries"
          :courses="localCourseEntries"
          :quizzes="getQuizEntries"
          :exercises="getExerciseEntries"
          :labs="getLabEntries"
          :iframes="getIFrameEntries"
          :posibleLeafs="posibleLeafs"
          :canEditLeafs="false"
          title="Learning Path"
          :groupers="groupers"
          :groupersCanAddLeaf="groupersCanAddLeaf"
          :showLinkToContent="true"
          @delete-structure="deleteLPathStructure"
          @add-group="addGroup"
          @add-leaf="addLeaf"
          @delete-group="deleteGroup"
          @delete-leaf="deleteLeaf"
          @rename-group="renameGroup"
        />
      </div>
    </div>
    <div class="w-2/5 pl-10 content-right-panel" sticky-container>
      <div class="flex flex-wrap mb-10" v-if="isEditing">
        <div class="w-full content-status">
          <label class="vs-input--label">Status</label>
          <v-select
            v-model="localLPath.status"
            :clearable="false"
            placeholder="Select one"
            :options="getLPathPossibleStatus"
            :reduce="status => status.value"
          />
        </div>
        <span v-if="isEditing" class="mt-2 text-grey my-ine-url">View this <i>in my.ine</i>: <a target="_blank" :href="`${myIneUrl}/path/${localLPath.id}`">{{localLPath.name}}</a></span>
      </div>
      <div class="flex flex-wrap mb-10" v-if="isEditing">
        <div class="w-full mb-5 published-date" v-if="localLPath.published_date">
          <label class="vs-input--label">Published on</label>
          <vs-input class="w-full" disabled :value="localLPath.published_date | dateFormat"></vs-input>
        </div>
        <div class="w-1/2 modified-date">
          <label class="vs-input--label">Modified on</label>
          <vs-input disabled :value="localLPath.modified | dateFormat"></vs-input>
        </div>
        <div class="w-1/2 pl-1 created-date">
          <label class="vs-input--label">Created on</label>
          <vs-input disabled :value="localLPath.created | dateFormat"></vs-input>
        </div>
      </div>
      <div class="flex action-buttons" v-sticky sticky-offset="{top: 25}" sticky-z-index="999">
        <vs-button class="w-full" color="primary" icon-pack="feather" @click="preSave">Save</vs-button>
      </div>
      <div class="mt-10 media-files">
        <label class="vs-input--label">Background Image</label>
        <vx-input-group class="w-full mb-8" v-if="localLPath.images">
          <template slot="prepend" v-if="localLPath.images.main_image_url">
            <div class="prepend-text">
              <img class="lpath-image" :src="localLPath.images.main_image_url" />
            </div>
          </template>
          <vs-input
            disabled
            v-model="localLPath.images.main_image_url"
          />
        </vx-input-group>
      </div>
      <div class="mt-10 relative">
        <label class="vs-input--label">Learning Path Trailer</label>
        <vs-input class="w-full" v-model="localLPath.trailer_jwplayer_id" description-text="JWPlayer media id, Example: 'mcAbYS1s'"></vs-input>
        <div v-if="localLPath.trailer_jwplayer_id">
          <video-preview :mediaId="localLPath.trailer_jwplayer_id" />
        </div>
      </div>
      <div class="mt-10 flex flex-wrap add-passes" v-if="isEditing">
        <passes-selector
          v-if="this.localLPath.access"
          :passes="this.localLPath.direct_passes"
          type="Learning Path"
          :entityId="localLPath.id"
        />
      </div>
    </div>
    <div class="right-sidebar">
      <vs-sidebar position-right  parent="body" default-index="1"  color="primary" class="sidebarx" spacer v-model="sidebarActive">
        <content-list
          :withSelection="true"
          :filterTypes="typeCapitalized"
          :itemsSelected="itemsSelected"
          @select-item="selectItem"
          :videoOptions="videoOptions"
        />
      </vs-sidebar>
    </div>
  </div>
</template>
<script>
import vSelect from "vue-select"
import cloneDeep from "lodash.clonedeep"
import Sticky from 'vue-sticky-directive'
import "flatpickr/dist/flatpickr.css"
import slug from "slug-generator"
import { mapGetters, mapActions } from "vuex"
import UploadFile from "../components/UploadFile"
import LPathStructure from "./forms/TreeStructure"
import { flatten, capitalize } from "@/utils/utils.js"
import { searchTags, getBasicData, warningUnsaved, searchLPaths, searchCollections } from "@/utils/mixins"
import Skills from '@/components/Skills'
import CONSTANTS from '@/constants'
import ContentList from './ContentList'
import PassesSelector from "./forms/PassesSelector"
import VideoPreview from '@/components/VideoPreview'

export default {
  mixins: [searchTags, getBasicData, warningUnsaved, searchLPaths, searchCollections],
  directives: {Sticky},
  components: {
    "v-select": vSelect,
    UploadFile,
    LPathStructure,
    ContentList,
    PassesSelector,
    VideoPreview,
    Skills,
  },
  data() {
    return {
      CONTENT_TYPES: CONSTANTS.CONTENT_TYPES,
      selectedId: null,
      selectedType: null,
      selectedParent: null,
      localLPath: {},
      currentCategories: [],
      configdateTimePicker: {
        enableTime: true,
        dateFormat: "m-d-Y H:i"
      },
      autoAlias: true,
      sidebarActive: false,
      importCsvErrors: null,
      rememberToSave: false,
      itemsSelected: {
        Course: [],
        Video: [],
        Quiz: [],
        Exercise: [],
        Lab: [],
        iFrame: [],
        Pass: []
      },
      courses: {},
      videos: {},
      quizzes: {},
      exercises: {},
      labs: {},
      iframes: {},
      localCourseEntries: {},
      videoOptions: ['lesson'],
      posibleLeafs: ['course', 'video', 'quiz', 'exercise', 'lab', 'iframe'],
      groupers: ['group'],
      groupersCanAddLeaf: ['group'],
      myIneUrl: process.env.VUE_APP_MY_INE_URL,
    }
  },
  computed: {
    ...mapGetters("app", ["getSelectedCompany"]),
    ...mapGetters("vendors", ["getVendors"]),
    ...mapGetters("categories", ["getCategories"]),
    ...mapGetters("lareas", ["getLAreas"]),
    ...mapGetters("videos", ["getVideoEntries", 'getCurrentVideo']),
    ...mapGetters("courses", ['getCurrentCourse']),
    ...mapGetters('quizzes', ['getQuizEntries', 'getCurrentQuiz']),
    ...mapGetters("exercises", ["getExerciseEntries", 'getCurrentExercise']),
    ...mapGetters("labs", ["getLabEntries", 'getCurrentLab']),
    ...mapGetters("iframes", ["getIFrameEntries", 'getCurrentIFrame']),
    ...mapGetters("lpaths", [
      "getStatusTransitions",
      "getCurrentLPath",
      "getLPathPossibleStatus",
      "getLPathDifficulties",
      "getLPathStructure"
    ]),
    canUploadCSV() {
      return (
        this.isEditing &&
        this.localLPath.content &&
        !this.localLPath.content.length
      );
    },
    relatedCollectionValue: {
      get() {
        const existing = this.localLPath.skill_dive_collections && this.localLPath.skill_dive_collections[0]
        if (!existing) return null
        if (typeof existing === "string") return existing
        return existing.uuid || existing.id || null
      },
      set(value) {
        if (!this.localLPath.skill_dive_collections) {
          this.$set(this.localLPath, "skill_dive_collections", [])
        }
        const option = this.collectionsOptions.find((collection) => collection.value === value)
        const cachedName = this.collectionsCache[value] || null
        const next = value
          ? {
              id: value,
              uuid: value,
              name: option ? option.label : cachedName,
            }
          : undefined
        if (next) {
          this.$set(this.localLPath.skill_dive_collections, 0, next)
          this.seedCollectionsCacheFromItems([next])
        } else {
          this.$set(this.localLPath, "skill_dive_collections", [])
          this.updateCollectionsCache([])
        }
      },
    },
    isEditing() {
      return this.$route.name === "edit-lp";
    },
    typeCapitalized () {
      if (this.selectedType === 'iframe') return ['iFrame']
      return this.selectedType ? [this.selectedType.charAt(0).toUpperCase() + this.selectedType.slice(1)] : []
    },
    fullCollectionsOptions() {
      return this.getFullCollectionsOptions()
    },
  },
  methods: {
    ...mapActions("videos", ["getAllVideos", 'saveVideoToEdit']),
    ...mapActions("courses", ["getAllCourses", 'saveCourseToEdit']),
    ...mapActions("quizzes", ["getAllQuizzes", 'saveQuizToEdit']),
    ...mapActions("exercises", ["getAllExercises", 'saveExerciseToEdit']),
    ...mapActions("labs", ["getAllLabs", 'saveLabToEdit']),
    ...mapActions("iframes", ["getAllIFrames", 'saveIFrameToEdit']),
    ...mapActions("lpaths", [
      "getLPathAction",
      "resetCurrentLPath",
      "saveLPathToApi",
      "uploadLPathCSV",
      "saveLPathToStore",
      'addLeafToContentStructure',
      'removeLeafToContentStructure',
      'addGroupToContentStructure',
      'removeGroupToContentStructure',
      'renameGroupOfContentStructure',
      'deleteContentStructure'
    ]),
    successUpload() {
      this.saveLPathToStore(this.localLPath);
      this.$vs.notify({
        color: "success",
        title: "Upload Success",
        text: "The file was uploaded successfully"
      });
    },
    errorUpload(error) {
      this.saveLPathToStore(this.localLPath);
      this.showError(error)
    },
    async uploadLPathCSVWrapper(formData) {
      try {
        this.$vs.loading();
        const response = await this.uploadLPathCSV(formData);
        await this.getLPathAction({ lpathId: this.$route.params.id });
        this.setLocalLPathFromStore();
        this.showUploadErrors(response.results);

        this.$vs.notify({
          color: "success",
          title: "Upload Success",
          text: "The file was uploaded successfully"
        });
      } catch (error) {
        this.showError(error)
      }
      await this.getAllLPathItems();
      this.$vs.loading.close();
    },
    showUploadErrors(results) {
      this.importCsvErrors = results.reduce((acc, e) => {
        if (e.result === "error") {
          acc.push([Object.values(e.errors).join(), e.s3_video_url]);
        }
        return acc;
      }, []);
    },
    preSave() {
      if (
        this.getCurrentLPath.status !== this.localLPath.status && 
        !this.getStatusTransitions[this.localLPath.status].includes(this.getCurrentLPath.status)
      ) {
        this.$vs.dialog({
          color: 'warning',
          title: `Warning`,
          text: `After saving you won't be able to return to ${this.getCurrentLPath.status} status`,
          accept: () => {this.confirmSave()}
        })
      } else {
        this.confirmSave()
      }
    },
    confirmSave () {
      this.saveLocalLPath()
    },
    async saveLocalLPath() {
      try {
        this.$vs.loading();

        this.saveLPathToStore(this.localLPath);

        const response = await this.saveLPathToApi();

        this.setLocalLPathFromStore();

        this.rememberToSave = false

        if (this.$route.name === "create-lp") {
          this.$router.push({
            name: "edit-lp",
            params: { id: response.id }
          });
        }

        this.$vs.notify({
          title: "Success",
          text: "Learning Path saved",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "success"
        });
      } catch (error) {
        this.showError(error)
      }
      this.$vs.loading.close();
    },
    selectItem(item) {
      switch (item.type) {
        case 'Course':
          this.$set(this.courses, item.item.id, item.item)
          this.saveCourseToEdit(item.item)
          break
        case 'Video':
          this.$set(this.videos, item.item.id, item.item)
          this.saveVideoToEdit(item.item)
          break
        case 'Quiz':
          this.$set(this.quizzes, item.item.id, item.item)
          this.saveQuizToEdit(item.item)
          break
        case 'Exercise':
          this.$set(this.exercises, item.item.id, item.item)
          this.saveExerciseToEdit(item.item)
          break
        case 'Lab':
          this.$set(this.labs, item.item.id, item.item)
          this.saveLabToEdit(item.item)
          break
        case 'iFrame':
          this.$set(this.iframes, item.item.id, item.item)
          this.saveIFrameToEdit(item.item)
          break
      }
      this.itemsSelected[item.type].push(item.item.id)
      this.addLeafToStructure({type: item.type.toLowerCase(), item: item})
    },
    getDeepArrayIds(item, type) {
      try {
        let input = JSON.parse(JSON.stringify(item));
        let res = input.map(function f(o) {
          return o.content_type === type
            ? o.uuid
            : o.content && o.content.length
            ? o.content.map(f)
            : [];
        });
        res = [...new Set(flatten(res, true))];
        return res;
      } catch (error) {
        this.showError(error)
      }
    },
    getDeepArrayEntries(item, type) {
      try {
        let input = JSON.parse(JSON.stringify(item));
        let res = input.reduce(function f(acc, o) {
          if (o.content_type === type) {
            acc[o.uuid] = o
            return acc
          } else if (o.content && o.content.length) {
            return o.content.reduce(f, acc)
          } else {
            return acc
          }
        }, {});
        return res;
      } catch (error) {
        this.showError(error)
      }
    },
    async getAllLPathItems() {
      if (this.localLPath.content && this.localLPath.content.length) {
        const allStructureVideos = this.getDeepArrayIds(this.localLPath.content, "video")
        const allStructureQuizzes = this.getDeepArrayIds(this.localLPath.content, "quiz")
        const allStructureExercises = this.getDeepArrayIds(this.localLPath.content, "exercise")
        const allStructureLabs = this.getDeepArrayIds(this.localLPath.content, "lab")
        const allStructureIFrames = this.getDeepArrayIds(this.localLPath.content, "iframe")
        // Extract courses info from learning path content items.
        this.localCourseEntries = this.getDeepArrayEntries(this.localLPath.content, "course")
        this.itemsSelected.Course = Object.keys(this.localCourseEntries)

        if (allStructureVideos.length) {
          await this.getAllVideos({
            ids: allStructureVideos,
            page_size: allStructureVideos.length.toString()
          });
          this.itemsSelected.Video = allStructureVideos
        }
        if (allStructureQuizzes.length) {
          await this.getAllQuizzes({
            ids: allStructureQuizzes,
            page_size: allStructureQuizzes.length.toString()
          });
          this.itemsSelected.Quiz = allStructureQuizzes
        }
        if (allStructureExercises.length) {
          await this.getAllExercises({
            ids: allStructureExercises,
            page_size: allStructureExercises.length.toString()
          });
          this.itemsSelected.Exercise = allStructureExercises
        }
        if (allStructureLabs.length) {
          await this.getAllLabs({
            ids: allStructureLabs,
            page_size: allStructureLabs.length.toString()
          });
          this.itemsSelected.Lab = allStructureLabs
        }
        if (allStructureIFrames.length) {
          await this.getAllIFrames({
            ids: allStructureIFrames,
            page_size: allStructureIFrames.length.toString()
          });
          this.itemsSelected.iFrame = allStructureIFrames
        }
      }
    },
    closeSidebar(event) {
      this.sidebarActive = event;
    },
    // @params: {oldName, newName, type}
    renameGroup(e) {
      this.renameGroupOfContentStructure(e)
      this.rememberToSave = true
    },
    // @params: {parent, index, type}
    addGroup(e) {
      this.addGroupToContentStructure(e)
      this.rememberToSave = true
    },
    // @params: {parent, index}
    deleteGroup(e) {
      this.removeGroupToContentStructure(e)
      this.itemsSelected.Video = this.getDeepArrayIds(this.localLPath.content, "video")
      this.itemsSelected.Quiz = this.getDeepArrayIds(this.localLPath.content, "quiz")
      this.itemsSelected.Exercise = this.getDeepArrayIds(this.localLPath.content, "exercise")
      this.itemsSelected.Lab = this.getDeepArrayIds(this.localLPath.content, "lab")
      this.itemsSelected.iFrame = this.getDeepArrayIds(this.localLPath.content, "iframe")
      this.itemsSelected.Course = this.getDeepArrayIds(this.localLPath.content, "course")
      this.rememberToSave = true
    },
    addLeaf(e) {
      this.selectedParent = e.parent;
      this.selectedId = "";
      this.selectedType = e.type;
      this.sidebarActive = true;
    },
    async addLeafToStructure({type}) {
      // add getCurrentVideo to the selected parent
      switch (type.toLowerCase()) {
        case "video":
          // add video to the list to render
          // have to modified structure in a mutation
          this.addLeafToContentStructure({
            parent: this.selectedParent, 
            leaf: {
              uuid: this.getCurrentVideo.id,
              content_type: type.toLowerCase()
            }
          })
          this.selectedId = this.getCurrentVideo.id
          this.rememberToSave = true
          break;
        case "quiz":
          // add quiz to the list to render
          // have to modified structure in a mutation
          this.addLeafToContentStructure({
            parent: this.selectedParent, 
            leaf: {
              uuid: this.getCurrentQuiz.id,
              content_type: type.toLowerCase()
            }
          })
          this.selectedId = this.getCurrentQuiz.id
          this.rememberToSave = true
          break;
        case "exercise":
          // add exercise to the list to render
          // have to modified structure in a mutation
          this.addLeafToContentStructure({
            parent: this.selectedParent, 
            leaf: {
              uuid: this.getCurrentExercise.id,
              content_type: type.toLowerCase()
            }
          })
          this.selectedId = this.getCurrentExercise.id
          this.rememberToSave = true
          break;
        case "lab":
          // add lab to the list to render
          // have to modified structure in a mutation
          this.addLeafToContentStructure({
            parent: this.selectedParent, 
            leaf: {
              uuid: this.getCurrentLab.id,
              content_type: type.toLowerCase()
            }
          })
          this.selectedId = this.getCurrentLab.id
          this.rememberToSave = true
          break;
        case "iframe":
          // add iframe to the list to render
          // have to modified structure in a mutation
          this.addLeafToContentStructure({
            parent: this.selectedParent, 
            leaf: {
              uuid: this.getCurrentIFrame.id,
              content_type: type.toLowerCase()
            }
          })
          this.selectedId = this.getCurrentIFrame.id
          this.rememberToSave = true
          break;
        case "course":
          // add course to the list to render
          // have to modified structure in a mutation
          this.addLeafToContentStructure({
            parent: this.selectedParent, 
            leaf: {
              uuid: this.getCurrentCourse.id,
              content_type: type.toLowerCase()
            }
          })
          this.localCourseEntries = {...this.localCourseEntries, 
            [this.getCurrentCourse.id]: {...this.getCurrentCourse}
          }
          this.selectedId = this.getCurrentCourse.id
          this.rememberToSave = true
          break;
        default:
          break;
      }
      this.sidebarActive = false;
    },
    deleteLeaf(e) {
      this.removeLeafToContentStructure(e)
      switch (e.item.content_type) {
        case 'video':
          this.itemsSelected.Video = this.getDeepArrayIds(this.localLPath.content, "video")
          break;
        case 'quiz':
          this.itemsSelected.Quiz = this.getDeepArrayIds(this.localLPath.content, "quiz")
          break;
          case 'exercise':
          this.itemsSelected.Exercise = this.getDeepArrayIds(this.localLPath.content, "exercise")
          break;
          case 'lab':
          this.itemsSelected.Lab = this.getDeepArrayIds(this.localLPath.content, "lab")
          break;
          case 'iframe':
          this.itemsSelected.iFrame = this.getDeepArrayIds(this.localLPath.content, "iframe")
          break;
        case 'course':
          this.itemsSelected.Course = this.getDeepArrayIds(this.localLPath.content, "course")
          break;
        default:
          break;
      }
      this.rememberToSave = true
    },
    deleteLPathStructure() {
      this.itemsSelected = {
        Course: [],
        Video: [],
        Quiz: [],
        Exercise: [],
        Lab: [],
        iFrame: [],
        Pass: []
      },
      this.deleteContentStructure()
      this.setLocalLPathFromStore()
      this.rememberToSave = true
    },
    capitalize (value) {
      return value ? [capitalize(value)] : []
    },
    setLocalLPathFromStore() {
      this.localLPath = cloneDeep(this.getCurrentLPath || {})
    },
    async getLPathToEdit(lpathId = '') {
      this.$vs.loading()
      await this.getLPathAction({ lpathId: lpathId })
      this.setLocalLPathFromStore();
      if (this.localLPath.company.id !== this.getSelectedCompany) {
        // ToDo: Possible bug, we could still be in create
        this.$router.push({ name: "create-lp" });
        this.$vs.notify({
          title: "Warning",
          text: 'Learning path you were editing belongs to a different company',
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning"
        });
      }
      await this.getAllLPathItems();
      this.$vs.loading.close()
    },
    getNextLearningPath() {
      if (this.localLPath.next_learning_path_item) {
        this.lpathsOptions.push({ label: this.localLPath.next_learning_path_item.name, value: this.localLPath.next_learning_path_item.id })
      }
    },
    clearSearch() {
      this.clearCollectionsOptions()
      return true
    }
  },
  async mounted() {
    this.$vs.loading({
      container: "#lpath-async-api-data-2",
      scale: 0.6
    });
    try {
      // @mixin
      await this.getBasicData();

    } catch (error) {
      this.$vs.loading.close("#lpath-async-api-data-2 > .con-vs-loading");
      this.showError(error)
    }
    this.$vs.loading.close("#lpath-async-api-data-2 > .con-vs-loading");
  },
  async created() {
    if (this.$route && this.$route.params.id) {
      if (this.$route.params.id !== this.localLPath.id) {
        try {
          await this.getLPathToEdit(this.$route.params.id)
          this.getNextLearningPath()
        } catch (error) {
          this.showError(error)
          this.$router.push({ name: "create-lp" });
        }
      }
    } else {
      this.resetCurrentLPath();
      this.setLocalLPathFromStore();
    }
  },
  watch: {
    "localLPath.skill_dive_collections": function(newValue) {
      this.seedCollectionsCacheFromItems(newValue)
    },
    "localLPath.name": function(newValue) {
      if (this.autoAlias) {
        this.localLPath.slug = slug(newValue);
      }
    },
    "getCurrentLPath.content": function() {
      this.localLPath.content = this.getCurrentLPath.content
    },
    autoAlias: function(newValue) {
      if (newValue) {
        this.localLPath.slug = slug(this.localLPath.name);
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === "create-lp") {
      this.resetCurrentLPath();
    }
    if (to.name === 'edit-lp') {
      this.getLPathToEdit(to.params.id)
    }
    this.setLocalLPathFromStore();
    next();
  }
};
</script>
<style lang="scss">
.lpath-form {
  .con-img-upload {
    padding: 0;
  }
  .content-left-panel {
    .con-img-upload {
      .img-upload {
        height: 100px;
        width: 100px;
      }
      .text-archive {
        font-size: 0.8em;
      }
    }
  }
  .content-right-panel {
    .con-img-upload {
      display: flex;
      .img-upload {
        height: 80px;
        margin: 0;
      }
    }
  }
  .con-input-upload {
    margin: 0;
    width: 100%;
    height: 80px;
  }
  .content-right-panel {
    .con-input-upload {
      height: 80px;
    }
  }
  .import-csv-errors {
    list-style-type: disc;
  }
  .media-files {
    .prepend-text {
      padding: 0;
      .lpath-image {
        width: 53px;
      }
    }
  }
}
.vs-sidebar-position-right {
  max-width: 60%;
  padding: 30px;
  .vs-sidebar--items {
    overflow: auto;
  }
}

.vs-con-loading__container {
  position: relative;
  overflow: unset;
}
</style>
